<template>
  <main id="app" class="app-grid">
    <Navigation
      id="header"
      :sections="{}"
      :otherRoutes="otherRoutes"
      currentSection="about"
      :showHome="true"
      :wasHome="wasHome"
      :showRule="false"
      :class="{ unfolded: !isFolded, folded: isFolded }"
      backAlt="Back Home"
    />
    <router-link
      to="/"
      title="Back Home"
      class="logo logo-fixed"
      :class="{ simple: notIntro }"
    >
      <AnimeLogo />
    </router-link>
    <div class="fold-menu" :class="{ unfolded: !isFolded, folded: isFolded }">
      <button id="button-fold" class="button-fold action" @click="handleFold">
        <ion-icon name="arrow-back" size="large" v-pre>Fold Menu</ion-icon>
      </button>
      <button
        id="button-unfold"
        class="button-unfold action"
        @click="handleUnfold"
      >
        <ion-icon name="menu" size="large" v-pre>Menu</ion-icon>
      </button>
    </div>
    <section
      id="intro"
      class="profile-bg"
      v-observe-visibility="{
        callback: sectionChanged
      }"
    >
      <img
        class="cover right top rellax large-profile"
        data-rellax-speed="-4"
        src="@/assets/img/profile-large.jpg"
        alt="Profile Picture - Duo Yang"
      />
      <div class="credit">
        <p class="small right">
          Photo by
          <br />
          <b>Xiaozhi Yu</b>
        </p>
      </div>
    </section>
    <section class="introline line-2">
      <hr class="stroke color-pri rellax" data-rellax-speed="-2" />
    </section>
    <section class="about section">
      <div class="heading">
        <p class="overline text-second">About</p>
        <h2 class="title">Duo Yang</h2>
        <p class="text-second compact" lang="zh-Hans">杨铎</p>
      </div>
      <div class="content">
        <p>
          I am a graduate student from University of Michigan studying
          <strong class="highlight">Human-Computer Interaction</strong> for my
          master's degree. Before that, my bachelor's degree is in Industrial
          Engineering at Tsinghua University. Coming from an interdisciplinary
          background, I have the opportunity to become an explorer in new ways
          of thinking, new approaches to design, and new technologies to work
          with.
        </p>
        <p>
          Always passionate about user experience, my mission is to continue my
          <strong class="highlight">exploration</strong> in creating synergy
          between design and technology and to build great experiences and
          creative solutions.
        </p>
      </div>
      <ul class="content compact">
        <h4>As a designer</h4>
        <li>
          I am good at creating
          <strong class="highlight">interaction design</strong> that is coherent
          and visually refined.
        </li>
        <li>
          I always hold my high standards in design details. Only a design that
          is solid in details can scale well as a design system.
        </li>
        <li>
          I believe that great accessibility for a larger audience is what makes
          a design great.
        </li>
      </ul>
      <ul class="content compact">
        <h4>As an engineer</h4>
        <li>I strive for consistency and scalability in what I build.</li>
        <li>
          I am a firm believer in great teamwork. Designs can only be as great
          as how it is built.
        </li>
        <li>
          I
          <strong class="highlight">prototype</strong> my ideas and designs, and
          build products that work.
        </li>
      </ul>
      <ul class="content compact">
        <h4>As an explorer</h4>
        <li>
          I am always curious to learn about new approaches and new
          technologies.
        </li>
        <li>
          I enjoy the process of testing out new ideas and figuring out how to
          make them work.
        </li>
        <li>
          I believe in the power of even a small
          <strong class="highlight">observation</strong>. It is where great
          insights start from.
        </li>
      </ul>
    </section>
    <Footer id="footer" :leftCopy="true" />
  </main>
</template>

<style lang="scss">
@import '@/styles/base.scss';

.line-2 {
  grid-area: 1 / -2 / span 1 / span 1;
  position: relative;
}

.profile-bg {
  grid-area: 1 / 1 / span 1 / -1;
  height: $logo-height + $logo-trans-top + $line-height * 9;
  position: relative;
  overflow-y: hidden;

  img {
    height: 150%;
    width: 150%;
    margin: 0;
    z-index: -1;
  }

  .credit {
    position: absolute;
    right: 0;
    bottom: 0;
    color: $text-light-third;
    padding: $baseline;

    p {
      line-height: $line-height * 0.85;
    }
  }
}

.summary {
  h4 {
    margin-top: $line-height;
    margin-bottom: 0;
  }
}

// Media query
@media only screen and (max-width: $small-width - 1) {
  .large-profile {
    -o-object-position: center top !important;
    object-position: center top !important;
    align-self: center !important;
  }
}
@media only screen and (min-width: $small-width) {
  .line-2 {
    grid-column: -3 / span 2;
  }
  .profile-bg {
    grid-column-start: 3;
  }
}
@media only screen and (min-width: $mid-width) {
  .line-2 {
    grid-column-start: 3;
  }
  .profile-bg {
    grid-column-start: 4;
  }
}
@media only screen and (min-width: $large-width) {
  .line-2 {
    grid-column-start: 4;
  }
  .profile-bg {
    grid-column-start: 5;
  }
}
</style>

<script>
import Navigation from '@/components/Navigation.vue'
import Footer from '@/components/Footer.vue'
import AnimeLogo from '@/components/animation/logo.vue'
import Rellax from 'rellax'

export default {
  name: 'about',
  components: {
    Navigation,
    AnimeLogo,
    Footer
  },
  data() {
    return {
      routes: this.$store.state.nav.routes,
      isFolded: true,
      notIntro: false
    }
  },
  computed: {
    otherRoutes() {
      let res = {}
      Object.assign(res, this.routes)
      delete res.home
      delete res.about
      return res
    },
    wasHome() {
      return this.$store.state.nav.lastRouteName === 'home'
    }
  },
  methods: {
    sectionChanged(isVisible, entry) {
      if (isVisible === this.notIntro) {
        this.notIntro = !isVisible
      }
    },
    handleFold() {
      this.isFolded = true
    },
    handleUnfold() {
      this.isFolded = false
    }
  },
  mounted() {
    new Rellax('.rellax')
  }
}
</script>
